import { useCallback, useMemo, useState } from "react";
import { useVoiceSettings } from "../useVoiceSettings";
import { Button } from "V2.0/common/button";
import { Switch } from "V2.0/common/switch";
import InputComponent from "V2.0/components/InputComponent";

enum NoiseEnvironment {
  OFFICE = "office",
  CALL_CENTER = "call_center",
  STREET = "street",
  RESTAURANT = "restaurant",
  HALLOWEEN = "halloween",
  CHRISTMAS = "christmas",
  SALSA = "salsa",
}

const noiseEnvironmentLabels = {
  [NoiseEnvironment.OFFICE]: "Office",
  [NoiseEnvironment.CALL_CENTER]: "Call Center",
  [NoiseEnvironment.HALLOWEEN]: "Halloween",
  [NoiseEnvironment.RESTAURANT]: "Restaurant",
  [NoiseEnvironment.STREET]: "Street",
  [NoiseEnvironment.CHRISTMAS]: "Christmas",
  [NoiseEnvironment.SALSA]: "Salsa",
};

interface BackgroundNoiseSettings {
  backgroundNoiseEnabled: boolean;
  backgroundNoiseVolumeFactor: number;
  backgroundNoiseEnvironment: NoiseEnvironment;
}

interface BackgroundAmbienceProps {
  backgroundNoiseSettings: BackgroundNoiseSettings;
  workspaceId: string;
  assistantId: string;
  locked: boolean;
  syncAssistant: (data: object) => void;
}

function getAmbienceEnvironmentOptions() {
  return Object.values(NoiseEnvironment).map((environment, idx) => {
    return {
      id: idx,
      value: environment,
      label: noiseEnvironmentLabels[environment],
    };
  });
}

function useBackgroundAmbience(backgroundNoiseSettings) {
  const [backgroundNoiseSettingsState, setBackgroundNoiseSettingsState] =
    useState<BackgroundNoiseSettings>({
      backgroundNoiseEnabled: backgroundNoiseSettings.backgroundNoiseEnabled,
      backgroundNoiseVolumeFactor:
        backgroundNoiseSettings.backgroundNoiseVolumeFactor,
      backgroundNoiseEnvironment:
        backgroundNoiseSettings.backgroundNoiseEnvironment,
    });

  const updateSettingsState = useCallback(function (
    updatedSetting: Partial<BackgroundNoiseSettings>
  ) {
    setBackgroundNoiseSettingsState((prevSettingsState) => {
      return {
        ...prevSettingsState,
        ...updatedSetting,
      };
    });
  },
  []);

  const toggleBackgroundNoise = useCallback(function (isEnabled) {
    updateSettingsState({
      backgroundNoiseEnabled: isEnabled,
    });
  }, []);

  const updateNoiseVolumeFactor = useCallback(function (value) {
    console.log({});
    updateSettingsState({
      backgroundNoiseVolumeFactor: value,
    });
  }, []);

  const updateAmbienceEnvironment = useCallback(function (
    environment: NoiseEnvironment
  ) {
    updateSettingsState({
      backgroundNoiseEnvironment: environment,
    });
  },
  []);

  const value = useMemo(() => {
    return {
      ...backgroundNoiseSettingsState,
      updateAmbienceEnvironment,
      updateNoiseVolumeFactor,
      toggleBackgroundNoise,
    };
  }, [
    backgroundNoiseSettingsState,
    updateAmbienceEnvironment,
    updateNoiseVolumeFactor,
    toggleBackgroundNoise,
  ]);

  return value;
}

export default function BackgroundAmbience(props: BackgroundAmbienceProps) {
  const {
    backgroundNoiseSettings,
    workspaceId,
    assistantId,
    locked,
    syncAssistant,
  } = props;

  const {
    backgroundNoiseEnabled,
    toggleBackgroundNoise,
    backgroundNoiseEnvironment,
    updateAmbienceEnvironment,
    backgroundNoiseVolumeFactor,
    updateNoiseVolumeFactor,
  } = useBackgroundAmbience(backgroundNoiseSettings);

  const { updateVoiceSettings } = useVoiceSettings({
    syncAssistant,
    workspaceId,
    assistantId,
  });

  const handleSave = async function () {
    const payload = {
      backgroundNoiseSettings: {
        backgroundNoiseEnabled,
        backgroundNoiseEnvironment,
        backgroundNoiseVolumeFactor,
      },
    };

    updateVoiceSettings(payload);
  };

  return (
    <div className="flex flex-col border rounded-lg w-3/5 min-w-[600px] m-4">
      <div className="p-4 bg-gray-50 rounded-t-lg px-6">
        <p className="text-base font-semibold text-gray-900">
          Background Ambience
        </p>
        <p className="text-sm mt-1 text-gray-500">
          Configure background sounds to make your call feel natural
        </p>
      </div>
      <div className="px-6 p-4 grid grid-cols-3">
        <div className="col-span-3 space-y-8">
          <div className="flex flex-col gap-y-2">
            <div className="flex items-center gap-x-2">
              <p className="text-sm font-medium">Use Background Ambience</p>
              <Switch
                color="emerald"
                checked={backgroundNoiseEnabled}
                onClick={() => toggleBackgroundNoise(!backgroundNoiseEnabled)}
                disabled={locked}
              />
            </div>
          </div>

          <div className="grid grid-cols-4 ml-4 space-y-6">
            <div className="col-span-4 xl:col-span-3">
              <InputComponent
                type="select"
                label="Ambience"
                description="Pick an ambience that fits the Call scenario"
                disabled={locked || !backgroundNoiseEnabled}
                options={getAmbienceEnvironmentOptions()}
                value={backgroundNoiseEnvironment}
                onChange={(e) => updateAmbienceEnvironment(e.target.value)}
              />
            </div>
            <div className="col-span-4 xl:col-span-3">
              <InputComponent
                type="slider"
                disabled={!backgroundNoiseEnabled || locked}
                label="Ambience Volume"
                description="Controls how loud the background ambience sounds"
                value={backgroundNoiseVolumeFactor}
                min={0.2}
                max={1}
                step={0.1}
                sliderMarks={{
                  0.2: "Low",
                  0.6: "Medium",
                  1: "High",
                }}
                onChange={(e) => updateNoiseVolumeFactor(e.currentTarget.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-b-lg px-4 py-3 text-right">
        {!locked && <Button onClick={handleSave}>Save</Button>}
      </div>
    </div>
  );
}
